/**
 * Content previews: how a message looks to the recipient on each channel (D-53).
 *
 * Email HTML is rendered in a sandboxed iframe: no scripts, a unique origin, and a CSP that only lets images,
 * styles and fonts load. Links open in a new tab, and the canvas is white like an email client, in both themes.
 */
import type { Channel, ChannelItem } from "@aiyo/shared";
import { type ReactNode, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useElementWidth } from "../lib/hooks";
import { Segmented } from "./filters";
import { Code, CodeBlock } from "./ui";

type Payload = Record<string, unknown>;

const str = (p: Payload, key: string) => (typeof p[key] === "string" && p[key] ? (p[key] as string) : null);

/** Preview of one channel item. `to` is the recipient address when it is known. */
export function ChannelPreview({
  channel,
  payload,
  to,
  appName,
}: {
  channel: Channel;
  payload: Payload;
  to?: string | null;
  appName?: string;
}) {
  switch (channel) {
    case "email":
      return (
        <EmailPreview
          subject={str(payload, "subject")}
          html={str(payload, "html")}
          text={str(payload, "text")}
          fromName={str(payload, "from_name")}
          replyTo={str(payload, "reply_to")}
          to={to}
        />
      );
    case "sms":
      return <SmsPreview text={str(payload, "text") ?? ""} />;
    case "whatsapp":
      return (
        <WhatsappPreview
          template={str(payload, "template")}
          language={str(payload, "language")}
          variables={Array.isArray(payload.variables) ? (payload.variables as string[]) : []}
        />
      );
    default:
      return (
        <PushPreview
          title={str(payload, "title")}
          body={str(payload, "body")}
          image={str(payload, "image")}
          data={(payload.data ?? null) as Record<string, unknown> | null}
          appName={appName}
        />
      );
  }
}

/** Every channel item of a message, with tabs when there is more than one. */
export function MessageContent({ items, appName }: { items: ChannelItem[]; appName?: string }) {
  const { t } = useTranslation();
  const [active, setActive] = useState(0);
  const item = items[Math.min(active, items.length - 1)];
  if (!item) return null;
  return (
    <div className="flex flex-col gap-4">
      {items.length > 1 ? (
        <Segmented
          label={t("preview.channel")}
          value={String(active)}
          onChange={(v) => setActive(Number(v))}
          options={items.map((i, index) => ({ id: String(index), label: t(`channels.${i.type}`) }))}
        />
      ) : null}
      <ChannelPreview channel={item.type} payload={item.payload as Payload} appName={appName} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Push
// ---------------------------------------------------------------------------

export function PushPreview({
  title,
  body,
  image,
  data,
  appName,
}: {
  title: string | null;
  body: string | null;
  image: string | null;
  data?: Record<string, unknown> | null;
  appName?: string;
}) {
  const { t } = useTranslation();
  const dataKeys = Object.keys(data ?? {});
  const silent = !title && !body;
  return (
    <div className="flex flex-col gap-3">
      <div className="rounded-3xl bg-surface-secondary p-4 shadow-sm">
        <div className="flex items-center gap-2 text-xs text-muted">
          <span className="size-4 shrink-0 rounded-md bg-accent" aria-hidden />
          <span className="truncate font-medium">{appName ?? t("preview.yourApp")}</span>
          <span aria-hidden>·</span>
          <span>{t("preview.now")}</span>
        </div>
        {silent ? (
          <p className="mt-2 text-sm text-muted">
            {dataKeys.length ? t("preview.dataOnly") : t("preview.pushEmpty")}
          </p>
        ) : (
          <div className="mt-2 flex min-w-0 flex-col gap-0.5">
            {title ? <div className="text-sm font-semibold break-words">{title}</div> : null}
            {body ? (
              <p className="line-clamp-4 text-sm break-words whitespace-pre-line text-muted">{body}</p>
            ) : null}
          </div>
        )}
        {image && !silent ? (
          <img src={image} alt="" className="mt-3 max-h-48 w-full rounded-xl object-cover" />
        ) : null}
      </div>
      {dataKeys.length ? (
        <div className="flex flex-wrap items-center gap-1.5 text-xs text-muted">
          {t("preview.dataKeys")}
          {dataKeys.map((k) => (
            <Code key={k}>{k}</Code>
          ))}
        </div>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Email
// ---------------------------------------------------------------------------

const EMAIL_CSP = [
  "default-src 'none'",
  "img-src https: http: data: cid:",
  "style-src 'unsafe-inline' https:",
  "font-src https: data:",
  "media-src https:",
].join("; ");

const EMAIL_BASE_STYLE =
  "html{background:#fff;color-scheme:light}" +
  "body{margin:0;color:#1f2328;font:14px/1.5 -apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Helvetica,Arial,sans-serif}" +
  "img{max-width:100%}";

/**
 * The email as a standalone document for the preview frame. The gateway's tags go first in <head>, so
 * the email's own styles still win, as they would in a mail client.
 */
export function emailDocument(html: string): string {
  const head =
    `<meta charset="utf-8"><meta http-equiv="Content-Security-Policy" content="${EMAIL_CSP}">` +
    `<meta name="referrer" content="no-referrer"><base target="_blank"><style>${EMAIL_BASE_STYLE}</style>`;
  if (/<head[\s>]/i.test(html)) return html.replace(/<head([^>]*)>/i, `<head$1>${head}`);
  if (/<html[\s>]/i.test(html)) return html.replace(/<html([^>]*)>/i, `<html$1><head>${head}</head>`);
  return `<!doctype html><html><head>${head}</head><body><div style="padding:16px">${html}</div></body></html>`;
}

type EmailView = "html" | "text" | "source";
type Width = "desktop" | "mobile";

/** Viewport widths the email is laid out at: a desktop reading pane and a phone. */
const DEVICE_WIDTH: Record<Width, number> = { desktop: 720, mobile: 375 };
const FRAME_HEIGHT = 560;

/**
 * The email laid out at the device width, then scaled down to fit the space available, so a 600px
 * template looks as it would in a desktop client instead of being cut off by a narrow panel.
 */
function EmailFrame({ title, doc, device }: { title: string; doc: string; device: number }) {
  const { t } = useTranslation();
  const [box, setBox] = useState<HTMLDivElement | null>(null);
  const available = useElementWidth(box);
  const scale = available > 0 ? Math.min(1, available / device) : 1;
  return (
    <div className="flex flex-col items-center gap-2 bg-default p-3">
      <div ref={setBox} className="flex w-full justify-center">
        <div
          className="shrink-0 overflow-hidden rounded-lg border border-border bg-white shadow-sm"
          style={{ width: device * scale, height: FRAME_HEIGHT }}
        >
          <iframe
            title={title}
            srcDoc={doc}
            sandbox="allow-popups allow-popups-to-escape-sandbox"
            referrerPolicy="no-referrer"
            className="block border-0 bg-white"
            style={{
              width: device,
              height: FRAME_HEIGHT / scale,
              transform: `scale(${scale})`,
              transformOrigin: "0 0",
            }}
          />
        </div>
      </div>
      {scale < 0.99 ? (
        <span className="text-xs text-muted">
          {t("preview.email.scaled", { width: device, percent: Math.round(scale * 100) })}
        </span>
      ) : null}
    </div>
  );
}

export function EmailPreview({
  subject,
  html,
  text,
  fromName,
  replyTo,
  to,
}: {
  subject: string | null;
  html: string | null;
  text: string | null;
  fromName?: string | null;
  replyTo?: string | null;
  to?: string | null;
}) {
  const { t } = useTranslation();
  const views: EmailView[] = [
    ...(html ? (["html"] as const) : []),
    ...(text ? (["text"] as const) : []),
    ...(html ? (["source"] as const) : []),
  ];
  const [view, setView] = useState<EmailView>(views[0] ?? "text");
  const [width, setWidth] = useState<Width>("desktop");
  const shown = views.includes(view) ? view : (views[0] ?? "text");
  const doc = useMemo(() => (html ? emailDocument(html) : ""), [html]);

  return (
    <div className="flex min-w-0 flex-col overflow-hidden rounded-2xl border border-border">
      <dl className="grid grid-cols-[max-content_minmax(0,1fr)] gap-x-4 gap-y-1.5 border-b border-border bg-surface-secondary px-4 py-3 text-sm">
        <HeaderRow label={t("preview.email.from")}>
          {fromName ? `${fromName} ` : null}
          <span className="text-muted">{t("preview.email.fromCredential")}</span>
        </HeaderRow>
        <HeaderRow label={t("preview.email.to")}>
          {to || <span className="text-muted">{t("preview.email.toRecipient")}</span>}
        </HeaderRow>
        {replyTo ? <HeaderRow label={t("preview.email.replyTo")}>{replyTo}</HeaderRow> : null}
        <HeaderRow label={t("preview.email.subject")}>
          <span className="font-semibold">
            {subject || <span className="font-normal text-muted">—</span>}
          </span>
        </HeaderRow>
      </dl>

      {views.length > 1 || shown === "html" ? (
        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border px-4 py-2">
          {views.length > 1 ? (
            <Segmented
              label={t("preview.email.view")}
              value={shown}
              onChange={setView}
              options={views.map((v) => ({ id: v, label: t(`preview.email.views.${v}`) }))}
            />
          ) : (
            <span />
          )}
          {shown === "html" ? (
            <Segmented
              label={t("preview.email.width")}
              value={width}
              onChange={setWidth}
              options={[
                { id: "desktop", label: t("preview.email.desktop") },
                { id: "mobile", label: t("preview.email.mobile") },
              ]}
            />
          ) : null}
        </div>
      ) : null}

      {shown === "html" ? (
        <EmailFrame title={subject ?? t("preview.email.frameTitle")} doc={doc} device={DEVICE_WIDTH[width]} />
      ) : shown === "source" ? (
        <div className="p-3">
          <CodeBlock value={html ?? ""} />
        </div>
      ) : (
        <pre className="max-h-[560px] overflow-auto bg-white p-4 font-sans text-sm whitespace-pre-wrap break-words text-[#1f2328]">
          {text}
        </pre>
      )}
    </div>
  );
}

function HeaderRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <>
      <dt className="text-muted">{label}</dt>
      <dd className="min-w-0 truncate">{children}</dd>
    </>
  );
}

// ---------------------------------------------------------------------------
// SMS
// ---------------------------------------------------------------------------

const GSM7 =
  "@£$¥èéùìòÇ\nØø\rÅåΔ_ΦΓΛΩΠΨΣΘΞÆæßÉ !\"#¤%&'()*+,-./0123456789:;<=>?¡ABCDEFGHIJKLMNOPQRSTUVWXYZÄÖÑÜ§¿abcdefghijklmnopqrstuvwxyzäöñüà";
const GSM7_EXTENDED = "^{}\\[~]|€\f";

/** Characters and SMS parts: 160/153 per part in GSM-7, 70/67 when any character needs Unicode. */
export function smsParts(text: string): { chars: number; parts: number; unicode: boolean } {
  let units = 0;
  let unicode = false;
  for (const ch of text) {
    if (GSM7.includes(ch)) units += 1;
    else if (GSM7_EXTENDED.includes(ch)) units += 2;
    else unicode = true;
  }
  if (unicode) units = text.length;
  const [single, multi] = unicode ? [70, 67] : [160, 153];
  const parts = units === 0 ? 0 : units <= single ? 1 : Math.ceil(units / multi);
  return { chars: units, parts, unicode };
}

export function SmsPreview({ text }: { text: string }) {
  const { t } = useTranslation();
  const info = smsParts(text);
  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-col items-start gap-1 rounded-3xl bg-surface-secondary p-4">
        <div className="max-w-[85%] rounded-2xl rounded-bl-md bg-surface px-3.5 py-2 text-sm break-words whitespace-pre-wrap shadow-sm">
          {text || <span className="text-muted">—</span>}
        </div>
      </div>
      <p className="text-xs text-muted">
        {t("preview.sms.count", { chars: info.chars, parts: info.parts })}
        {info.unicode ? ` · ${t("preview.sms.unicode")}` : ""}
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// WhatsApp
// ---------------------------------------------------------------------------

export function WhatsappPreview({
  template,
  language,
  variables,
}: {
  template: string | null;
  language: string | null;
  variables: string[];
}) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col gap-3 rounded-3xl bg-surface-secondary p-4">
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <span className="text-muted">{t("preview.whatsapp.template")}</span>
        {template ? <Code>{template}</Code> : <span className="text-muted">—</span>}
        {language ? <span className="text-xs text-muted">({language})</span> : null}
      </div>
      {variables.length ? (
        <ol className="flex flex-col gap-1 text-sm">
          {variables.map((v, i) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: template variables are positional, {{n}} is their identity.
            <li key={i} className="flex gap-2">
              <Code>{`{{${i + 1}}}`}</Code>
              <span className="min-w-0 break-words">{v}</span>
            </li>
          ))}
        </ol>
      ) : null}
      <p className="text-xs text-muted">{t("preview.whatsapp.hint")}</p>
    </div>
  );
}
